import { ArrowBack, ArrowForward, ExpandMore } from '@mui/icons-material/';
import {
  Apps,
  WorkOutline,
  DeviceHub,
  Business,
  AccountTree,
  Extension,
  PeopleAlt,
  Computer,
  SettingsSuggest,
  PlaylistAddCheck,
  HourglassFull,
} from '@mui/icons-material/';
import { Accordion, AccordionSummary, AccordionDetails, ListItemIcon, ListItemText } from '@mui/material/';
import { LogoKappaWhite, LogoKappaWhiteRounded } from 'Images/index';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useTranslation from 'Hooks/useTranslation';
import { useSelector } from 'react-redux';
import { userSelector } from 'Redux/LoggedUserSlice';
import rolesApi from 'Api/axios/roles-api';
import { usePermissionStore } from 'stores/permissions-store';
import { useDrawerStore } from 'stores/drawer-store';
import { colors } from 'Constants/Colors';
import styled from '@emotion/styled';

const drawerWidth = 240;

interface StyledDrawerProps {
  open: boolean;
}

const StyledDrawer = styled.div<StyledDrawerProps>(({ open }) => ({
  width: open ? drawerWidth : `calc(72px + 1px)`,
  flexShrink: 0,
  // Permitir que los textos largos hagan wrap
  whiteSpace: 'normal',
  boxSizing: 'border-box',
  backgroundColor: colors.BLACK,
  transition: 'width 0.3s ease',
  height: '100vh',
  // Mantener visible al hacer scroll sin superponerse al contenido
  position: 'sticky',
  top: 0,
  alignSelf: 'flex-start',
  overflowX: 'hidden',
  overflowY: 'auto',
}));

const DrawerHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 8,
});

const StyledList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
});

interface StyledListItemButtonProps {
  isSelected: boolean;
}

const StyledListItemButton = styled.button<StyledListItemButtonProps>(({ isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '12px 16px',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  color: 'white',
  fontFamily: 'metropolis',
  // Evitar que el texto se corte: permitir wrap y no encimarse con el borde
  '& .MuiListItemText-root': {
    flex: '1 1 auto',
    minWidth: 0,
  },
  '& .MuiTypography-root': {
    color: 'white',
    fontFamily: 'metropolis',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& .MuiListItemIcon-root': {
    display: 'flex',
    justifyContent: 'center',
    color: colors.GREEN,
    marginRight: 16,
  },
  '&:hover': {
    background: `linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, ${colors.GREEN_SHINING} 100%)`,
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
    '& .MuiListItemIcon-root': {
      color: colors.WHITE,
    },
  },
  ...(isSelected && {
    background: `linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, ${colors.GREEN_SHINING} 100%)`,
    borderRight: `3px solid ${colors.GREEN}`,
    '& .MuiTypography-root': {
      fontWeight: 'bold',
      color: colors.WHITE,
    },
    '& .MuiListItemIcon-root': {
      color: colors.WHITE,
    },
  }),
}));

const StyledArrowButton = styled(StyledListItemButton)({
  marginTop: '30px',
  '& .MuiTypography-root': {
    color: colors.WHITE,
  },
  '& .MuiListItemIcon-root': {
    color: colors.GREEN,
  },
  '&:hover': {
    background: `linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, ${colors.GREEN_SHINING} 100%)`,
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
    '& .MuiListItemIcon-root': {
      color: colors.WHITE,
    },
  },
});

const StyledAccordion = styled(Accordion)({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  margin: '0 !important',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    padding: 0,
    minHeight: 'auto',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '& .MuiAccordionDetails-root': {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

interface StyledSubMenuItemProps {
  isSelected: boolean;
}

const StyledSubMenuItem = styled.button<StyledSubMenuItemProps>(({ isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '10px 16px 10px 32px',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  color: 'white',
  fontFamily: 'metropolis',
  fontSize: '14px',
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: colors.GREEN,
  },
  '&:hover': {
    background: `linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, ${colors.GREEN_SHINING} 100%)`,
    '& .MuiSvgIcon-root': {
      color: colors.WHITE,
    },
  },
  ...(isSelected && {
    background: `linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, ${colors.GREEN_SHINING} 100%)`,
    borderRight: `3px solid ${colors.GREEN}`,
    fontWeight: 'bold',
    '& .MuiSvgIcon-root': {
      color: colors.WHITE,
    },
  }),
}));

const ImageStyleClosed = styled.img({
  width: '60%',
  margin: 'auto',
  marginTop: '15%',
  marginBottom: '15%',
});

const ImageStyleOpen = styled.img({
  margin: 'auto',
  marginTop: '10%',
  marginBottom: '5%',
  width: '100%',
});

export const Drawer = () => {
  const history = useHistory();
  const open = useDrawerStore(state => state.open);
  const toggleOpen = useDrawerStore(state => state.toggle);
  const { t2 } = useTranslation('SideBar');
  const { roleId } = useSelector(userSelector);

  // Store de permisos crudos; el mapeo y ordenado se hace aquí
  const rawPermissions: any[] = usePermissionStore(state => state.permissions);
  const setPermissions = usePermissionStore(state => state.setPermissions);

  // Estado para controlar el acordeón de Reportes
  const [reportsExpanded, setReportsExpanded] = useState(false);

  /**
   * Representa un subitem dentro de un menú con acordeón.
   */
  type SubMenuItem = { route: string; textKey: string; icon: React.ReactNode };

  /**
   * Representa un item renderizable dentro del Drawer.
   * - icon: nodo React del icono a mostrar.
   * - route: ruta a navegar al hacer clic (vacío si tiene subItems).
   * - text: etiqueta ya traducida a mostrar.
   * - subItems: lista opcional de subitems para menús acordeón.
   */
  type MenuItem = {
    icon: React.ReactNode;
    route: string;
    text: string;
    subItems?: { route: string; text: string; icon: React.ReactNode }[];
  };

  /**
   * Configuración de cada opción del menú.
   * - id: prefijo de permiso (o id lógico para items sin permiso).
   * - route: ruta de navegación (vacío si tiene subItems).
   * - icon: icono a mostrar.
   * - textKey: clave de traducción para SideBar.json.
   * - requiresPermission: si true, se mostrará solo si el backend asignó el permiso correspondiente (id).
   * - order: prioridad de orden ascendente. Menor número aparece antes.
   * - subItems: lista opcional de subitems para menús acordeón.
   */
  type MenuConfig = {
    id: string;
    route: string;
    icon: React.ReactNode;
    textKey: string;
    requiresPermission: boolean;
    order: number;
    subItems?: SubMenuItem[];
  };

  // Config legible con control por "order".
  const menuConfig: MenuConfig[] = useMemo(
    () => [
      // Panel Principal: siempre arriba de todos (order más bajo)
      {
        id: 'dashboard',
        route: '/dashboard',
        icon: <Apps />,
        textKey: 'Dashboard',
        requiresPermission: false,
        order: 0,
      },
      // Preferidos al inicio
      {
        id: 'employee_development',
        route: '/empleado/desarrollo',
        icon: <PeopleAlt />,
        textKey: 'Profile',
        requiresPermission: false,
        order: 10,
      },
      {
        id: 'my_courses',
        route: '/mis-cursos',
        icon: <Computer />,
        textKey: 'MyCourses',
        requiresPermission: false,
        order: 20,
      },
      // Resto de módulos
      {
        id: 'job_title',
        route: '/puestos',
        icon: <WorkOutline />,
        textKey: 'JobTitle',
        requiresPermission: true,
        order: 40,
      },
      {
        id: 'department',
        route: '/departamentos',
        icon: <DeviceHub />,
        textKey: 'Departments',
        requiresPermission: true,
        order: 50,
      },
      {
        id: 'enterprise',
        route: '/empresa',
        icon: <Business />,
        textKey: 'Company',
        requiresPermission: true,
        order: 60,
      },
      {
        id: 'course_definition',
        route: '/definicion',
        icon: <AccountTree />,
        textKey: 'DefinitionCourses',
        requiresPermission: true,
        order: 70,
      },
      {
        id: 'report',
        route: '',
        icon: <Extension />,
        textKey: 'reports',
        requiresPermission: true,
        order: 80,
        subItems: [
          { route: '/reporte/empleados', textKey: 'reportsCurrent', icon: <PlaylistAddCheck /> },
          { route: '/reporte/historial/empleados', textKey: 'reportsHistory', icon: <HourglassFull /> },
        ],
      },
      {
        id: 'employee',
        route: '/empleados',
        icon: <PeopleAlt />,
        textKey: 'Employees',
        requiresPermission: true,
        order: 90,
      },
      {
        id: 'course',
        route: '/cursos',
        icon: <Computer />,
        textKey: 'Courses',
        requiresPermission: true,
        order: 100,
      },
      {
        id: 'roles_permissions',
        route: '/roles',
        icon: <SettingsSuggest />,
        textKey: 'Management',
        requiresPermission: true,
        order: 110,
      },
    ],
    []
  );

  /**
   * Construye la lista final de ítems visibles según permisos y orden.
   * - Siempre incluye el Panel Principal primero (order=0).
   * - Si aún no llegan permisos, muestra Perfil + Mis cursos + Panel Principal.
   */
  const menuItems: MenuItem[] = useMemo(() => {
    const assigned = new Set(
      (rawPermissions || [])
        .filter(p => p?.assigned)
        .map(p => (typeof p.key === 'string' ? p.key.split('.')[0] : ''))
        .filter(Boolean)
    );

    const mapConfigToMenuItem = (cfg: MenuConfig): MenuItem => ({
      icon: cfg.icon,
      route: cfg.route,
      text: t2(cfg.textKey),
      subItems: cfg.subItems?.map(sub => ({
        route: sub.route,
        text: t2(sub.textKey),
        icon: sub.icon,
      })),
    });

    // Si permisos aún no cargan: mostrar Perfil + Mis cursos + Panel Principal
    if (!rawPermissions?.length) {
      return menuConfig
        .filter(cfg => cfg.id === 'dashboard' || cfg.id === 'my_courses' || cfg.id === 'employee_development')
        .sort((a, b) => a.order - b.order)
        .map(mapConfigToMenuItem);
    }

    // Filtrar por permisos y ordenar por prioridad
    return menuConfig
      .filter(cfg => !cfg.requiresPermission || assigned.has(cfg.id))
      .sort((a, b) => a.order - b.order)
      .map(mapConfigToMenuItem);
  }, [rawPermissions, menuConfig, t2]);

  /**
   * Carga de permisos del rol actual para decidir qué módulos se muestran.
   * Guardamos los permisos crudos en el store global y el mapeo a menú se
   * realiza en este componente.
   */
  useEffect(() => {
    if (!roleId) return;
    rolesApi.readPermissions(roleId).then(response => {
      if (response.data) {
        // Guardamos los permisos crudos; el mapeo a menú se hace localmente
        setPermissions(response.data.permissions);
      }
    });
  }, [roleId]);

  /**
   * Determina si la ruta dada debe mostrarse como seleccionada en el menú.
   * Considera coincidencia exacta o con subrutas.
   */
  const isRouteSelected = (route: string): boolean => {
    const currentPath = history.location.pathname;

    // Caso especial: Reportes Actuales incluye tanto /reporte/empleados como /reporte/cursos
    if (route === '/reporte/empleados') {
      return (
        currentPath === '/reporte/empleados' ||
        currentPath.startsWith('/reporte/empleados/') ||
        currentPath === '/reporte/cursos' ||
        currentPath.startsWith('/reporte/cursos/')
      );
    }

    // Caso especial: Reportes Historial incluye tanto /reporte/historial/empleados como /reporte/historial/cursos
    if (route === '/reporte/historial/empleados') {
      return (
        currentPath === '/reporte/historial/empleados' ||
        currentPath.startsWith('/reporte/historial/empleados/') ||
        currentPath === '/reporte/historial/cursos' ||
        currentPath.startsWith('/reporte/historial/cursos/')
      );
    }

    return currentPath === route || currentPath.startsWith(`${route}/`);
  };

  /**
   * Verifica si alguna subruta de reportes está activa para expandir el acordeón.
   */
  const isReportsActive = (): boolean => {
    const currentPath = history.location.pathname;
    return currentPath.startsWith('/reporte/');
  };

  /**
   * Render principal del Drawer (menú lateral).
   */
  return (
    <StyledDrawer open={open}>
      <DrawerHeader>
        {open ? (
          <ImageStyleOpen src={LogoKappaWhite} alt="icono" />
        ) : (
          <ImageStyleClosed src={LogoKappaWhiteRounded} alt="icono" />
        )}
      </DrawerHeader>

      <StyledList>
        {menuItems.map(item => {
          // Si el item tiene subItems, renderizar como acordeón
          if (item.subItems && item.subItems.length > 0) {
            const isAnySubItemSelected = isReportsActive();
            return (
              <StyledAccordion
                key={item.text}
                expanded={open && (reportsExpanded || isAnySubItemSelected)}
                onChange={() => setReportsExpanded(!reportsExpanded)}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={open ? <ExpandMore sx={{ color: colors.GREEN }} /> : null}
                  sx={{ padding: 0, minHeight: 'auto' }}
                >
                  <StyledListItemButton
                    isSelected={isAnySubItemSelected}
                    onClick={e => {
                      if (!open) {
                        // Si el drawer está cerrado, navegar al primer subitem
                        history.push(item.subItems![0].route);
                        e.stopPropagation();
                      }
                    }}
                    as="div"
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    {open && <ListItemText primary={item.text} />}
                  </StyledListItemButton>
                </AccordionSummary>
                {open && (
                  <AccordionDetails>
                    {item.subItems.map(subItem => (
                      <StyledSubMenuItem
                        key={subItem.route}
                        isSelected={isRouteSelected(subItem.route)}
                        onClick={() => history.push(subItem.route)}
                      >
                        {subItem.icon}
                        {subItem.text}
                      </StyledSubMenuItem>
                    ))}
                  </AccordionDetails>
                )}
              </StyledAccordion>
            );
          }

          // Item normal sin submenú
          const isSelected = isRouteSelected(item.route);
          return (
            <StyledListItemButton key={item.text} isSelected={isSelected} onClick={() => history.push(item.route)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              {open && <ListItemText primary={item.text} />}
            </StyledListItemButton>
          );
        })}
        <StyledArrowButton isSelected={false} onClick={toggleOpen}>
          <ListItemIcon>{open ? <ArrowBack /> : <ArrowForward />}</ListItemIcon>
          {open && <ListItemText primary={t2('HideMenu')} />}
        </StyledArrowButton>
      </StyledList>
    </StyledDrawer>
  );
};
